import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import { buildService } from '@rd-web-markets/shared/dist/services/service';

const api = buildService('/character_replacements', {
  only: ['all', 'create', 'update', 'delete']
});

const characterReplacementsService = {
  useFetchAll: (setLoading) => {
    const [characterReplacements, setCharacterReplacements] = useState([]);
    const [totalPages, setTotalPages] = useState(null);
    const location = useLocation();

    const fetchCharacterReplacements = useErrorHandling(
      useCallback(
        async (params = {}) => {
          setLoading && setLoading(true);
          const response = await api.all(params);
          setCharacterReplacements(response?.character_replacements || []);
          setTotalPages(Number(response?.pages) || null);
          setLoading && setLoading(false);
        },
        [setLoading]
      ),
      useCallback(async () => {
        setLoading && setLoading(false);
      }, [setLoading])
    );

    useEffect(() => {
      const urlSearchParams = new URLSearchParams(location.search);

      const params = { page: urlSearchParams.get('page') || 1 };

      const queryParam = urlSearchParams.get('query');
      if (queryParam) {
        params.query = queryParam;
      }

      fetchCharacterReplacements(params);
    }, [location.search, fetchCharacterReplacements]);

    return [characterReplacements, setCharacterReplacements, fetchCharacterReplacements, totalPages];
  },

  useCreate: (setLoading, setCharacterReplacements) => {
    const createCharacterReplacement = useErrorHandling(
      useCallback(async (data) => {
        setLoading && setLoading(true);
        const response = await api.create({ character_replacement: data });
        setCharacterReplacements(prev => [response, ...prev]);
        return response;
      }, [setLoading, setCharacterReplacements]),
      useCallback(async () => {
        setLoading && setLoading(false);
      }, [setLoading])
    );

    return createCharacterReplacement;
  },

  useUpdate: (setLoading, setCharacterReplacements) => {
    const updateCharacterReplacement = useErrorHandling(
      useCallback(async ({ id, data }) => {
        setLoading && setLoading(true);
        const response = await api.update(id, { character_replacement: data });
        setCharacterReplacements(prev => prev.map(c => c.id === id ? response : c));
        return response;
      }, [setLoading, setCharacterReplacements]),
      useCallback(async () => {
        setLoading && setLoading(false);
      }, [setLoading])
    );

    return updateCharacterReplacement;
  },

  useDelete: (setLoading, setCharacterReplacements) => {
    const deleteCharacterReplacement = useErrorHandling(
      useCallback(async (id) => {
        setLoading && setLoading(true);
        await api.delete(id);
        setCharacterReplacements(prev => prev.filter(c => c.id !== Number(id)));
        setLoading && setLoading(false);
      }, [setLoading, setCharacterReplacements]),
      useCallback(async () => {
        setLoading && setLoading(false);
      }, [setLoading])
    );

    return deleteCharacterReplacement;
  }
};

characterReplacementsService.useCrud = (setLoading) => {
  const [characterReplacements, setCharacterReplacements, fetchCharacterReplacements, totalPages] =
    characterReplacementsService.useFetchAll(setLoading);

  const createCharacterReplacement = characterReplacementsService.useCreate(
    setLoading, setCharacterReplacements
  );
  const updateCharacterReplacement = characterReplacementsService.useUpdate(
    setLoading, setCharacterReplacements
  );
  const deleteCharacterReplacement = characterReplacementsService.useDelete(
    setLoading, setCharacterReplacements
  );

  return {
    characterReplacements,
    setCharacterReplacements,
    fetchCharacterReplacements,
    totalPages,
    createCharacterReplacement,
    updateCharacterReplacement,
    deleteCharacterReplacement
  };
};

export default characterReplacementsService;
